// Co-op sessions: people sharing a page, its filters and their pointers. A session is a ROOM. An invite
// carries the inviter's room, so two pairs working at the same time stay apart. Turning co-op on without
// an invite joins whoever is already in co-op on that page, or starts a room of your own.
//
// The page and the filters follow the group: whoever last moved it themselves (went to a page, changed a
// filter) leads and everyone else follows. Following never counts as leading, so someone catching up can't
// drag the leader back to the page they just left, and a page's default filters, loaded as a follower
// arrives, can't wipe the ones the leader picked (planCoopSync).
//
// Scrolling follows the same rule: whoever last scrolled by their own hand (wheel, touch, keys, the
// scrollbar) leads, and a scroll made by following never does, so two people scrolling at once don't fight.
// On a page you went to yourself, where you landed leads too.
//
// A pointer travels as a spot on the element under it (CoopAnchor), so it lands on the same row or button
// for everyone whatever their window size or scroll; the viewport fraction is the fallback. A scroll
// position travels the same way (CoopScrollPos): the element at the top-left of what's showing, with the
// fractions of the scroll range as the fallback.
import { z } from 'zod';
/** how long a participant counts as present after their last sync. */
export const COOP_STALE_MS = 6000;
export const COOP_ROOM_RE = /^[A-Za-z0-9_-]{6,40}$/;
// where an anchor path starts: a landmark of the shell, or a row / boat / SAP the page marks up
const ANCHOR_KEY_RE = /^(?:main|#nav-rail|\.topbar|\[role="dialog"\]|\[data-(?:row-id|vessel-id|sap)="[\w-]{1,40}"\])$/;
/** a key is used as a CSS selector on the other side, so only these shapes are accepted. */
export const isCoopAnchorKey = (k) => ANCHOR_KEY_RE.test(k);
/** the element a pointer is over: a stable key, the child-index path from it, the target's tag (a check
 *  that the path still lands on the same kind of element) and where in its box the pointer is (0..1). */
export const CoopAnchorSchema = z.object({
    k: z.string().max(80).refine(isCoopAnchorKey),
    p: z.array(z.number().int().min(0).max(5000)).max(40),
    t: z.string().regex(/^[A-Z][A-Z0-9-]{0,15}$/),
    ax: z.number().min(0).max(1),
    ay: z.number().min(0).max(1),
});
/** which scroller: `main` (the page itself), or an inner scroller's name (its data-coop-scroll or first
 *  class). Used in a selector on the other side, so plain names only. */
export const COOP_SCROLLER_RE = /^(?:main|[A-Za-z][\w-]{0,39})$/;
/** where one scroller is scrolled to: which scroller (`s`, and `i`, its place among those with that name
 *  on the page), the element at the top-left corner of what's showing of it (`a`, with ax / ay where the
 *  corner falls in it) and how far along the scroll range it is (fx, fy: the fallback, and the ends
 *  exactly; null when it doesn't scroll that way on the sender's screen). */
export const CoopScrollPosSchema = z.object({
    s: z.string().regex(COOP_SCROLLER_RE),
    i: z.number().int().min(0).max(99),
    a: CoopAnchorSchema.nullable(),
    fx: z.number().min(0).max(1).nullable(),
    fy: z.number().min(0).max(1).nullable(),
});
/** a page's scroll: the page itself first (when it scrolls), then the grids inside it that scroll. */
export const CoopScrollSchema = z.array(CoopScrollPosSchema).max(4);
/** a click, shown to the others as a ripple on what was clicked; `n` tells one click from the next. */
export const CoopClickSchema = z.object({ a: CoopAnchorSchema, n: z.number().int().min(0) });
/** how long a click is still shown to the others. */
export const COOP_CLICK_MS = 2500;
/** one co-op heartbeat: where I am, how I got there, my pointer, my shared filters and scroll, and what I'm
 *  doing (a click, the field I'm in, my last save). */
export const CoopSyncSchema = z.object({
    path: z.string().max(200),
    /** the room I'm in. Left out: the server picks (whoever's in co-op on this page, or a new room). */
    room: z.string().regex(COOP_ROOM_RE).optional().catch(undefined),
    /** I'm on this page because I followed the group there, not because I went there myself. */
    followed: z.boolean().optional(),
    x: z.number(),
    y: z.number(),
    anchor: CoopAnchorSchema.nullish().catch(null),
    /** the top-left of what I see of the grid my pointer is in, while I scroll with the room (CoopActivity). */
    frame: CoopAnchorSchema.nullish().catch(null),
    view: z.string().max(4000).optional(),
    /** my filters changed because I changed them (not a page's defaults, nor the group's being applied). */
    viewLead: z.boolean().optional(),
    /** my page's scroll. Left out (or malformed), what was stored for this page stays. */
    scroll: CoopScrollSchema.optional().catch(undefined),
    /** my scroll changed because I scrolled myself (not by following the group's). */
    scrollLead: z.boolean().optional(),
    /** how long before this heartbeat I last scrolled myself (ms): two people scrolling at once, the later
     *  hand leads, whichever heartbeat lands first. */
    scrollAge: z.number().int().min(0).max(60_000).optional().catch(undefined),
    /** my scroll is my own view (where I landed, or scrolled to before), not the group's I'm showing: should it
     *  change by itself (rows arriving), it keeps the lead if nobody has scrolled since I last led. */
    scrollOwn: z.boolean().optional(),
    /** a click since my last heartbeat. */
    click: CoopClickSchema.nullish().catch(null),
    /** the field I'm editing on the page. */
    focus: CoopAnchorSchema.nullish().catch(null),
    /** when I last saved something (my own clock; only compared with my earlier ones). */
    saved: z.number().int().min(0).optional().catch(undefined),
});
/** the room for someone who didn't name one: stay with the people you're with; otherwise join whoever is
 *  in co-op on this page (the biggest group). Two people who turn co-op on at the same moment each start a
 *  room of one, so between those the smaller id wins on both sides and they meet instead of swapping. */
function autoRoom(prev, others, path) {
    const mine = prev?.room ?? null;
    if (mine && others.some((r) => r.room === mine))
        return mine;
    const size = new Map();
    for (const r of others)
        size.set(r.room, (size.get(r.room) ?? 0) + 1);
    const here = [...new Set(others.filter((r) => r.path === path).map((r) => r.room))]
        .sort((a, b) => size.get(b) - size.get(a) || (a < b ? -1 : 1));
    const pick = here[0];
    if (pick && (!mine || size.get(pick) > 1 || pick < mine))
        return pick;
    return mine;
}
/** One heartbeat, decided: which room I'm in, my stored row, and what the room should follow.
 *  `rows` holds my stored row (any age) and everyone else's; rows older than COOP_STALE_MS don't count. */
export function planCoopSync(me, rows, input, now, newRoom) {
    const fresh = (r) => now - r.updatedAt <= COOP_STALE_MS;
    const stored = rows.find((r) => r.user === me) ?? null;
    const prev = stored && fresh(stored) ? stored : null;
    const others = rows.filter((r) => r.user !== me && r.room && fresh(r));
    const room = input.room ?? autoRoom(prev, others, input.path) ?? newRoom();
    const peers = others.filter((r) => r.room === room);
    // an omitted view (the moment a page is swapping) keeps what was stored rather than wiping it
    const view = input.view === undefined ? prev?.view ?? null : input.view;
    // filters that aren't my choice but match the room's carry the room's timestamp, not a new one: never
    // newer than the change they copy, yet they keep it alive should its owner reload and come back blank
    const echoAt = (v) => {
        let at = 0;
        for (const r of peers)
            if (v && r.view === v && r.viewAt > at)
                at = r.viewAt;
        return at;
    };
    // the scroll belongs to its page: an omitted one keeps what was stored for this page, and a new page
    // starts with none. A scroll that matches the room's (I followed it there) is an echo, like the filters.
    const samePage = !!prev && prev.room === room && prev.path === input.path;
    const scroll = input.scroll === undefined ? (samePage ? prev.scroll : null) : input.scroll;
    const scrollEchoAt = (v) => {
        let at = 0;
        for (const r of peers)
            if (v && r.path === input.path && r.scroll === v && r.scrollAt > at)
                at = r.scrollAt;
        return at;
    };
    let pathAt;
    let viewAt;
    let scrollAt;
    if (!prev || prev.room !== room) {
        // joining: a room of one leads itself; a newcomer follows the room's page, filters and scroll
        pathAt = peers.length ? 0 : now;
        viewAt = !view ? 0 : peers.length ? echoAt(view) : now;
        scrollAt = !scroll ? 0 : peers.length ? scrollEchoAt(scroll) : now;
    }
    else {
        pathAt = prev.path === input.path ? prev.pathAt : input.followed ? 0 : now;
        viewAt = (prev.view ?? null) === view ? prev.viewAt : input.viewLead ? now : echoAt(view);
        // on a page I went to myself, where I landed leads; on one the group took me to, I follow its scroll.
        // Scrolling I did on the last page leads nothing here.
        if (!samePage)
            scrollAt = !scroll ? 0 : input.scrollLead || !input.followed ? now : scrollEchoAt(scroll);
        else if ((prev.scroll ?? null) === scroll)
            scrollAt = prev.scrollAt;
        // a hand scroll counts from when the hand moved, not when the heartbeat carrying it landed
        else if (input.scrollLead)
            scrollAt = now - Math.min(input.scrollAge ?? 0, COOP_STALE_MS);
        // my own view changed by itself (rows arriving, a grid showing up): it stays the room's if it was, or
        // becomes it when nobody leads, but never over someone who scrolled after I last led
        else if (input.scrollOwn && scroll && !peers.some((r) => r.path === input.path && r.scrollAt > prev.scrollAt))
            scrollAt = now;
        else
            scrollAt = scrollEchoAt(scroll);
    }
    const row = { user: me, room, path: input.path, pathAt, view, viewAt, scroll, scrollAt, updatedAt: now };
    const group = [...peers, row];
    let followPath = null;
    let bestPath = -1;
    for (const r of group)
        if (r.pathAt > bestPath) {
            bestPath = r.pathAt;
            followPath = r.path;
        }
    let viewLead = null;
    for (const r of group) {
        if (r.path === followPath && r.view && r.viewAt > 0 && (!viewLead || r.viewAt > viewLead.viewAt))
            viewLead = r;
    }
    // the scroll leader: the last to scroll themselves on the room's page. Mine wins a tie (a follower's
    // echo carries the leader's own time), so whoever leads is never handed their own scroll back.
    let scrollLead = null;
    for (const r of [row, ...peers]) {
        if (r.path === followPath && r.scroll && r.scrollAt > 0 && (!scrollLead || r.scrollAt > scrollLead.scrollAt))
            scrollLead = r;
    }
    return {
        row,
        peers,
        followPath,
        followView: viewLead && viewLead.user !== me ? viewLead.view : null,
        followScroll: scrollLead && scrollLead.user !== me && input.path === followPath ? scrollLead.scroll : null,
    };
}
//# sourceMappingURL=coop.js.map